import type { EnemyDefinition, MapDefinition, Point } from '../../shared/game/world-types.js';
import { clearSegment, distance } from '../../shared/navigation/geometry.js';
import { PROP_RADIUS } from '../../shared/game/prop-footprints.js';
import { BLACK_HOOD } from './gangs.js';
import { coastEncounters, type EncounterPlan } from './coast-encounters.js';
import { memberUnit, type EncounterMember } from './roles.js';
import { coastPosts, type EncounterPost } from './coast-posts.js';
import { emberCoast } from '../maps/ember-coast.js';

const CREW_CLOTH = [0x784841, 0x505a45, 0x555363, 0x796344];
const LOOSE_CLUSTER: Point[] = [[-4, -1], [1, 2], [5, -2], [-2, 5], [4, 6], [-6, 3], [0, -5], [8, 2], [-5, -5], [0, 9]].map(([x, z]) => ({ x, z }));
/** Small goblins and minions still need the half-metre of open ground every other unit is checked against. */
const MIN_CLEARANCE = .5;
const FURNITURE = new Set(['sign', 'lamp-post', 'bridge', 'jetty', 'fence', 'banner', 'cave-mouth']);

/** Content placement avoids scenery as well as authoritative terrain and occupied bases. */
function placeNear(map: MapDefinition, desired: Point, radius: number, occupied: (Point & { radius: number })[]): Point {
  const safe = (p: Point) => clearSegment(map, p, p, radius) && occupied.every(other => distance(p, other) >= radius + other.radius + .65) &&
    (map.scenery?.props ?? []).every(prop => FURNITURE.has(prop.kind) || distance(p, prop) >= PROP_RADIUS[prop.kind] * (prop.scale ?? 1) * .6 + radius + .25);
  if (safe(desired)) return { ...desired };
  // Local deterministic repairs preserve the authored grouping.
  for (let ring = 1; ring <= 8; ring++) for (let i = 0; i < 24; i++) {
    const angle = i * Math.PI / 12 + ring * .31, point = { x: desired.x + Math.cos(angle) * ring, z: desired.z + Math.sin(angle) * ring };
    if (safe(point)) return point;
  }
  throw new Error('No clear encounter position near ' + desired.x + ', ' + desired.z + '.');
}

/** Each actor has an individual post; fixed roster order and ids are independent of formation. */
export function crewOf(plan: Pick<EncounterPlan, 'id' | 'name' | 'x' | 'z' | 'members'> & Partial<Pick<EncounterPlan, 'difficulty'>>, planIndex = 0, post?: EncounterPost, map?: MapDefinition): EnemyDefinition[] {
  const counts = new Map<EncounterMember, number>(), occupied: (Point & { radius: number })[] = [];
  const crew = plan.members.map((role, i): EnemyDefinition => {
    const n = counts.get(role) ?? 0;
    const { title, ...unit } = memberUnit(role, CREW_CLOTH[planIndex % CREW_CLOTH.length], planIndex + i, BLACK_HOOD);
    counts.set(role, n + 1);
    const offset = post?.positions[i] ?? LOOSE_CLUSTER[i % LOOSE_CLUSTER.length];
    const desired = { x: plan.x + offset.x, z: plan.z + offset.z };
    const spawn = map ? placeNear(map, desired, Math.max(unit.radius, MIN_CLEARANCE), occupied) : desired;
    occupied.push({ ...spawn, radius: unit.radius });
    return { ...unit, id: plan.id + '-' + role + '-' + n, encounterId: plan.id, ...plan.difficulty ? { encounterDifficulty: plan.difficulty } : {},
      name: plan.name + ' · ' + (role === 'slinger' ? 'Bandit' : title), spawn };
  });
  // Resolve walks after every stationary post is known, so lookout goals avoid the whole crew.
  for (const [i, enemy] of crew.entries()) {
    if (!post || post.activity === 'lookouts' && !post.lookouts?.includes(i)) continue;
    let waypoints = post.loop.map(point => ({ x: (post.activity === 'patrol' ? enemy.spawn.x : plan.x) + point.x,
      z: (post.activity === 'patrol' ? enemy.spawn.z : plan.z) + point.z }));
    if (map) waypoints = waypoints.map(point => placeNear(map, point, enemy.radius,
      post.activity === 'lookouts' ? occupied.filter(other => distance(other, enemy.spawn) > .01) : []));
    if (post.activity === 'lookouts') {
      const nearest = waypoints.reduce((best, point, index) => distance(point, enemy.spawn) < distance(waypoints[best], enemy.spawn) ? index : best, 0);
      waypoints = [...waypoints.slice(nearest), ...waypoints.slice(0, nearest)];
    }
    enemy.patrol = { waypoints, walkSpeed: 1.05 + planIndex % 3 * .1, pauseSeconds: post.activity === 'patrol' ? 1.5 : 2.5,
      startDelaySeconds: post.activity === 'patrol' ? i * .12 : 1 + i * .4 };
  }
  return crew;
}

export const coastEnemies: EnemyDefinition[] = coastEncounters.flatMap((plan, planIndex) => crewOf(plan, planIndex, coastPosts[plan.id], emberCoast));
